/**
 * SupervisionComplianceWidget — QP supervision compliance overview
 *
 * MN 256B.0659 Subd 8: QP supervision requirements
 * - Initial: 14 days
 * - Year 1: 90 days
 * - Year 2+: 120 days
 */

import React from 'react';
import {
  Card,
  Group,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendarDue,
  IconCheck,
  IconClipboardCheck,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_SUPERVISION_STATUS = gql`
  query GetSupervisionStatus($taskGroupRef: String, $limit: Int) {
    tasks(taskGroupRef: $taskGroupRef, limit: $limit) {
      id
      status
      deadlineAt
      completedAt
    }
  }
`;

interface SupervisionTask {
  id: string;
  status: string;
  deadlineAt: string | null;
  completedAt: string | null;
}

function getDaysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const deadline = new Date(dateStr);
  const today = new Date();
  const diffTime = deadline.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

interface SupervisionComplianceWidgetProps {
  title?: string;
  compact?: boolean;
}

export function SupervisionComplianceWidget({
  title = 'QP Supervision',
  compact = false,
}: SupervisionComplianceWidgetProps) {
  const { data: pendingData, loading: pendingLoading } = useQuery<{ tasks: SupervisionTask[] }>(
    GET_SUPERVISION_STATUS,
    { variables: { taskGroupRef: '4-30-15', status: 'PENDING', limit: 100 } }
  );

  const { data: completedData, loading: completedLoading } = useQuery<{ tasks: SupervisionTask[] }>(
    GET_SUPERVISION_STATUS,
    { variables: { taskGroupRef: '4-30-15', status: 'COMPLETED', limit: 100 } }
  );

  const loading = pendingLoading || completedLoading;
  const pendingTasks = pendingData?.tasks ?? [];
  const completedTasks = completedData?.tasks ?? [];

  // Calculate metrics
  const overdueCount = pendingTasks.filter(t => {
    const days = getDaysUntil(t.deadlineAt);
    return days !== null && days < 0;
  }).length;

  const dueSoonCount = pendingTasks.filter(t => {
    const days = getDaysUntil(t.deadlineAt);
    return days !== null && days >= 0 && days <= 14;
  }).length;

  const onTrackCount = pendingTasks.length - overdueCount - dueSoonCount;

  const totalDue = pendingTasks.length + completedTasks.length;
  const compliancePercent = totalDue > 0
    ? Math.round((completedTasks.length / totalDue) * 100)
    : 100;

  if (loading) {
    return (
      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed">Loading supervision data...</Text>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card withBorder padding="md">
        <Group justify="space-between">
          <Stack gap={0}>
            <Text size="sm" c="dimmed">{title}</Text>
            <Text size="xl" fw={700} c={compliancePercent >= 90 ? 'green' : compliancePercent >= 70 ? 'yellow' : 'red'}>
              {compliancePercent}%
            </Text>
            {overdueCount > 0 && (
              <Text size="xs" c="red">{overdueCount} overdue</Text>
            )}
          </Stack>
          <RingProgress
            size={60}
            thickness={6}
            roundCaps
            sections={[
              { value: compliancePercent, color: compliancePercent >= 90 ? 'green' : compliancePercent >= 70 ? 'yellow' : 'red' },
            ]}
          />
        </Group>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="violet">
            <IconClipboardCheck size={14} />
          </ThemeIcon>
          <Text fw={600}>{title}</Text>
        </Group>
        <RingProgress
          size={48}
          thickness={4}
          roundCaps
          sections={[{ value: compliancePercent, color: compliancePercent >= 90 ? 'green' : 'yellow' }]}
          label={
            <Text size="xs" ta="center" fw={700}>
              {compliancePercent}%
            </Text>
          }
        />
      </Group>

      <SimpleGrid cols={3} mb="md">
        <Stack gap={2} align="center">
          <ThemeIcon size="sm" variant="light" color="red">
            <IconAlertTriangle size={12} />
          </ThemeIcon>
          <Text size="xl" fw={700} c={overdueCount > 0 ? 'red' : undefined}>
            {overdueCount}
          </Text>
          <Text size="xs" c="dimmed">Overdue</Text>
        </Stack>
        <Stack gap={2} align="center">
          <ThemeIcon size="sm" variant="light" color="orange">
            <IconCalendarDue size={12} />
          </ThemeIcon>
          <Text size="xl" fw={700} c={dueSoonCount > 0 ? 'orange' : undefined}>
            {dueSoonCount}
          </Text>
          <Text size="xs" c="dimmed">Due Soon</Text>
        </Stack>
        <Stack gap={2} align="center">
          <ThemeIcon size="sm" variant="light" color="green">
            <IconCheck size={12} />
          </ThemeIcon>
          <Text size="xl" fw={700} c="green">{onTrackCount}</Text>
          <Text size="xs" c="dimmed">On Track</Text>
        </Stack>
      </SimpleGrid>

      <Stack gap="xs">
        <Text size="xs" c="dimmed">Completion Rate</Text>
        <Progress.Root size="lg">
          <Progress.Section value={compliancePercent} color="green">
            <Progress.Label>{completedTasks.length} done</Progress.Label>
          </Progress.Section>
          <Progress.Section value={100 - compliancePercent} color="gray">
            <Progress.Label>{pendingTasks.length} pending</Progress.Label>
          </Progress.Section>
        </Progress.Root>
      </Stack>
    </Card>
  );
}

export default SupervisionComplianceWidget;
